import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";
import { searchCities } from "../../api";
import { createRoute, getRoute, patchRoute } from "../../api/routes";
import { getCityDisplay } from "../../utils/city";

const emptyStop = () => ({
  key: `${Date.now()}-${Math.random()}`,
  id: null,
  city: null,
  address: "",
  arrival_time: "",
  departure_time: "",
});

const emptyFare = () => ({
  key: `${Date.now()}-${Math.random()}`,
  id: null,
  fromStopKeys: [],
  toStopKeys: [],
  price: "",
  childPrice: "",
});

const timeOrNull = (value) => {
  const raw = String(value || "").trim();
  return raw ? (raw.length === 5 ? `${raw}:00` : raw) : null;
};

const firstError = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return firstError(value[0]);
  if (typeof value === "object") return firstError(Object.values(value)[0]);
  return String(value);
};

const RouteEditPage = () => {
  const { t, i18n } = useTranslation("common");
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith("en") ? "en" : "uk";

  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [stops, setStops] = useState([emptyStop(), emptyStop()]);
  const [fares, setFares] = useState([emptyFare()]);
  const [cityOptions, setCityOptions] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await getRoute(id);
        if (cancelled) return;
        const route = resp.data;
        setName(route.name || "");
        setActive(route.active !== false);
        const loadedStops = (route.stops || []).map((stop, index) => ({
          key: String(stop.id || index),
          id: stop.id || null,
          city: stop.city || null,
          address: stop.address || "",
          arrival_time: (stop.arrival_time || "").slice(0, 5),
          departure_time: (stop.departure_time || "").slice(0, 5),
        }));
        setStops(
          loadedStops.length >= 2
            ? loadedStops
            : [...loadedStops, emptyStop()].slice(0, 2)
        );
        const loadedFares = (route.fares || []).map((fare, index) => ({
          key: String(fare.id || index),
          id: fare.id || null,
          fromStopKeys: (fare.from_stop_ids || []).map(String),
          toStopKeys: (fare.to_stop_ids || []).map(String),
          price: fare.price != null ? String(fare.price) : "",
          childPrice: fare.child_price != null ? String(fare.child_price) : "",
        }));
        setFares(loadedFares.length ? loadedFares : [emptyFare()]);
      } catch {
        if (!cancelled) setError(t("admin.routesLoadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  const searchStopCities = async (index, query) => {
    if (!query || query.trim().length < 2) {
      setCityOptions((prev) => ({ ...prev, [index]: [] }));
      return;
    }
    try {
      const rows = await searchCities(query.trim(), lang);
      setCityOptions((prev) => ({
        ...prev,
        [index]: Array.isArray(rows) ? rows : [],
      }));
    } catch {
      setCityOptions((prev) => ({ ...prev, [index]: [] }));
    }
  };

  const updateStop = (index, patch) => {
    setStops((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateFare = (index, patch) => {
    setFares((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const moveStop = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= stops.length) return;
    setStops((rows) => {
      const copy = [...rows];
      const tmp = copy[index];
      copy[index] = copy[next];
      copy[next] = tmp;
      return copy;
    });
  };

  const stopOptions = stops.filter((stop) => stop.city);
  const stopByKey = Object.fromEntries(stopOptions.map((stop) => [stop.key, stop]));

  const stopLabel = (stop) => {
    const order = stops.findIndex((row) => row.key === stop.key) + 1;
    const city = getCityDisplay(stop.city, lang);
    return order > 0 ? `${order}. ${city}` : city;
  };

  const save = async () => {
    setError("");
    const preparedStops = stops
      .filter((stop) => stop.city)
      .map((stop, index) => ({
        stop,
        order: index + 1,
        city: stop.city?.name || (typeof stop.city === "string" ? stop.city : ""),
      }))
      .filter((row) => row.city);
    if (preparedStops.length < 2) {
      setError(t("admin.routesNeedStops"));
      return;
    }
    const orderByKey = Object.fromEntries(
      preparedStops.map((row) => [row.stop.key, row.order])
    );
    const preparedFares = [];
    for (const fare of fares) {
      const fromKeys = fare.fromStopKeys.filter((key) => orderByKey[key]);
      const toKeys = fare.toStopKeys.filter((key) => orderByKey[key]);
      const price = String(fare.price || "").trim();
      const childPrice = String(fare.childPrice || "").trim();
      const blank = !fromKeys.length && !toKeys.length && !price && !childPrice;
      if (blank) continue;
      if (!fromKeys.length || !toKeys.length || !price) {
        setError(t("admin.routesNeedFareStops"));
        return;
      }
      preparedFares.push({
        ...(fare.id ? { id: fare.id } : {}),
        from_stop_ids: fromKeys.map((key) => stopByKey[key]?.id).filter(Boolean),
        to_stop_ids: toKeys.map((key) => stopByKey[key]?.id).filter(Boolean),
        from_orders: fromKeys.map((key) => orderByKey[key]),
        to_orders: toKeys.map((key) => orderByKey[key]),
        price,
        child_price: childPrice === "" ? "0" : childPrice,
      });
    }
    const payload = {
      name:
        name.trim() ||
        `${preparedStops[0].city} — ${preparedStops[preparedStops.length - 1].city}`,
      active,
      stops: preparedStops.map((row) => ({
        ...(row.stop.id ? { id: row.stop.id } : {}),
        city: row.city,
        address: row.stop.address.trim(),
        order: row.order,
        arrival_time: timeOrNull(row.stop.arrival_time),
        departure_time: timeOrNull(row.stop.departure_time),
      })),
      fares: preparedFares,
    };
    setSaving(true);
    try {
      if (isNew) {
        const resp = await createRoute(payload);
        navigate(`/administrator/routes/${resp.data.id}`, { replace: true });
      } else {
        await patchRoute(id, payload);
        navigate("/administrator/routes");
      }
    } catch (err) {
      const data = err?.response?.data;
      const detail =
        (typeof data === "string" && data) ||
        firstError(data?.fares) ||
        data?.name?.[0] ||
        data?.detail ||
        t("admin.routesSaveError");
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 3, maxWidth: 1100 }}>
      <Button
        component={RouterLink}
        to="/administrator/routes"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        {t("admin.nav.routes")}
      </Button>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        {isNew ? t("admin.routesNew") : t("admin.routesEdit")}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Stack spacing={2}>
        <TextField
          label={t("admin.route")}
          value={name}
          onChange={(event) => setName(event.target.value)}
          helperText={t("admin.routesNameHint")}
        />
        <FormControlLabel
          control={
            <Switch checked={active} onChange={(event) => setActive(event.target.checked)} />
          }
          label={t("admin.routesActive")}
        />
        <Typography fontWeight={600}>{t("admin.routesStops")}</Typography>
        {stops.map((stop, index) => (
          <Paper key={stop.key} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28 }}>
                  {index + 1}
                </Typography>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={cityOptions[index] || (stop.city ? [stop.city] : [])}
                  getOptionLabel={(city) => getCityDisplay(city, lang)}
                  value={stop.city}
                  onChange={(_e, value) => updateStop(index, { city: value })}
                  onInputChange={(_e, value, reason) => {
                    if (reason === "input") searchStopCities(index, value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.name === value?.name
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("admin.routesCity")} />
                  )}
                />
                <IconButton
                  size="small"
                  onClick={() => moveStop(index, -1)}
                  disabled={index === 0}
                  aria-label={t("admin.routesMoveUp")}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveStop(index, 1)}
                  disabled={index === stops.length - 1}
                  aria-label={t("admin.routesMoveDown")}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() =>
                    setStops((rows) =>
                      rows.length <= 2 ? rows : rows.filter((_, i) => i !== index)
                    )
                  }
                  disabled={stops.length <= 2}
                  aria-label={t("admin.delete")}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                label={t("admin.routesAddress")}
                value={stop.address}
                onChange={(event) => updateStop(index, { address: event.target.value })}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("admin.routesArrival")}
                  type="time"
                  value={stop.arrival_time}
                  onChange={(event) =>
                    updateStop(index, { arrival_time: event.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label={t("admin.routesDeparture")}
                  type="time"
                  value={stop.departure_time}
                  onChange={(event) =>
                    updateStop(index, { departure_time: event.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Stack>
          </Paper>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setStops((rows) => [...rows, emptyStop()])}
        >
          {t("admin.routesAddStop")}
        </Button>
        <Box>
          <Typography fontWeight={600}>{t("admin.routesFares")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("admin.routesFaresHint")}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>{t("admin.routesFareFrom")}</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>{t("admin.routesFareTo")}</TableCell>
                  <TableCell sx={{ width: 140 }}>{t("admin.routesPrice")}</TableCell>
                  <TableCell sx={{ width: 160 }}>{t("admin.routesChildPrice")}</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {fares.map((fare, index) => (
                  <TableRow key={fare.key}>
                    <TableCell>
                      <Autocomplete
                        multiple
                        size="small"
                        options={stopOptions}
                        getOptionLabel={stopLabel}
                        value={fare.fromStopKeys
                          .map((key) => stopByKey[key])
                          .filter(Boolean)}
                        onChange={(_e, value) =>
                          updateFare(index, {
                            fromStopKeys: value.map((stop) => stop.key),
                          })
                        }
                        isOptionEqualToValue={(option, value) => option.key === value.key}
                        renderInput={(params) => (
                          <TextField {...params} placeholder={t("admin.routesFareFrom")} />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        multiple
                        size="small"
                        options={stopOptions}
                        getOptionLabel={stopLabel}
                        value={fare.toStopKeys
                          .map((key) => stopByKey[key])
                          .filter(Boolean)}
                        onChange={(_e, value) =>
                          updateFare(index, {
                            toStopKeys: value.map((stop) => stop.key),
                          })
                        }
                        isOptionEqualToValue={(option, value) => option.key === value.key}
                        renderInput={(params) => (
                          <TextField {...params} placeholder={t("admin.routesFareTo")} />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={fare.price}
                        onChange={(event) =>
                          updateFare(index, { price: event.target.value })
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={fare.childPrice}
                        onChange={(event) =>
                          updateFare(index, { childPrice: event.target.value })
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setFares((rows) =>
                            rows.length <= 1 ? [emptyFare()] : rows.filter((_, i) => i !== index)
                          )
                        }
                        aria-label={t("admin.delete")}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setFares((rows) => [...rows, emptyFare()])}
            sx={{ mt: 1 }}
          >
            {t("admin.routesAddFare")}
          </Button>
        </Box>
        <Button variant="contained" onClick={save} disabled={saving}>
          {t("admin.save")}
        </Button>
      </Stack>
    </Box>
  );
};

export default RouteEditPage;
